/**
 * W5 operative-program model — the pure, JSX-free derivation logic the screen
 * and its tests share. The operative program is the ordered sequence of phases
 * that GOVERNS the plan curve (depth vs. days) read by Oxbo-Z §13 and the §21d
 * reconciler, so the seq→accumulated-time→curve derivation must be exact and
 * order-driven.
 *
 * Accumulated time (h) is the running sum of `time_hours` in Sequence order —
 * the X axis of the plan curve (÷24 = elapsed days). The curve's Y is each
 * phase's `depth_until`. Reordering the rows re-derives both. All functions are
 * total (null-safe) so a half-entered draft never throws.
 */
import type { ProgramaStep } from "@api/types";
import { asArray } from "@lib/asArray";

/** A step carrying its derived seq (1..N) + running accumulated time (h). */
export interface DerivedStep extends ProgramaStep {
  /** 1..N — the row's position in execution order (authoritative, re-derived). */
  seq: number;
  /** Running sum of time_hours through and including this step, in hours. */
  time_hours_accumulated: number;
}

/** Coerce a possibly-null numeric field to a finite number (0 when absent). */
function num(v: number | null | undefined): number {
  return v != null && Number.isFinite(v) ? v : 0;
}

/**
 * Order the steps by their wire `seq` (execution order), then re-stamp `seq` =
 * position + 1 and `time_hours_accumulated` = running sum of `time_hours`.
 *
 * Ordering by the wire `seq` (not JSON array order) is load-bearing: a real
 * backend without an `ORDER BY seq` can return the rows in any order, and this
 * curve feeds Oxbo-Z §13 + the §21d reconciler — trusting array order would
 * yield a silently-wrong plan curve. Once ordered, seq becomes 1..N by position
 * so an in-app reorder (which reorders the array) is honoured on the next
 * derive. `asArray` coerces a present-but-non-array body (`{}`, `null`, an error
 * envelope) to `[]` so a real-backend shape never `.map`-crashes the screen.
 */
export function withDerived(rows: unknown): DerivedStep[] {
  const ordered = [...asArray<ProgramaStep>(rows)].sort((a, b) => num(a.seq) - num(b.seq));
  let running = 0;
  return ordered.map((r, i) => {
    running += num(r.time_hours);
    return { ...r, seq: i + 1, time_hours_accumulated: running };
  });
}

/** Program totals shown in the KPI chips + table footer. */
export interface ProgramTotals {
  phases: number;
  /** Total time = the last row's accumulated time (h). */
  totalHours: number;
  /** Σ DOL (days on location) across phases. */
  totalDol: number;
  /** Final measured depth (m) = the deepest `depth_until` reached. */
  finalDepth: number;
  /** Σ amount (USD) across phases. */
  totalAmount: number;
  /** Elapsed days = totalHours / 24. */
  totalDays: number;
}

export function programTotals(rows: unknown): ProgramTotals {
  const derived = withDerived(rows);
  const last = derived[derived.length - 1];
  const totalHours = last ? last.time_hours_accumulated : 0;
  return {
    phases: derived.length,
    totalHours,
    totalDol: derived.reduce((s, r) => s + num(r.dol), 0),
    finalDepth: derived.reduce((m, r) => Math.max(m, num(r.depth_until)), 0),
    totalAmount: derived.reduce((s, r) => s + num(r.amount_usd), 0),
    totalDays: totalHours / 24,
  };
}

/** One plotted vertex of the plan curve: elapsed days × measured depth (m). */
export interface CurvePoint {
  seq: number;
  phase: string;
  /** Elapsed days at the END of this phase (accumulated hours ÷ 24). */
  days: number;
  /** Measured depth (m) reached at the end of this phase. */
  depth: number;
}

/**
 * Derive the plan curve from the ordered phases: one vertex per phase at
 * (accumulated days, depth_until), plus a leading origin vertex at the first
 * phase's `depth_from` on day 0 (spud). The X axis is always monotonic (time
 * only grows); depth may be flat or fall for non-drilling phases (punzados,
 * flowback), so the curve is NOT assumed monotonic in depth.
 */
export function curvePoints(rows: unknown): CurvePoint[] {
  const derived = withDerived(rows);
  if (derived.length === 0) return [];
  const first = derived[0]!;
  const origin: CurvePoint = {
    seq: 0,
    phase: "spud",
    days: 0,
    depth: num(first.depth_from),
  };
  return [
    origin,
    ...derived.map((r) => ({
      seq: r.seq,
      phase: r.phase,
      days: r.time_hours_accumulated / 24,
      depth: num(r.depth_until),
    })),
  ];
}

/** An empty step for the "+ add phase" affordance (seq is re-derived on commit). */
export function emptyStep(index: number): ProgramaStep {
  return {
    seq: index + 1,
    phase: "",
    code_description: null,
    depth_from: null,
    depth_until: null,
    time_hours: null,
    dol: null,
    pct_completion: null,
    amount_usd: null,
    time_hours_accumulated: null,
  };
}
